import { FunctionComponent, useRef, useState } from 'react';
import { KeyedMutator } from 'swr';
import {
  PopoverTrigger,
  PopoverNavigation,
  PopoverNavigationItem,
  PopoverNavigationLink,
  ButtonTertiary,
} from '@spotify-internal/encore-web/core';
import { useT } from '@spotify-internal/i18n-core';
import { sendEvent } from '@spotify-internal/s4mp-page-analytics';
import { useClipboard } from '@hooks/useClipboard';
import { Resource } from '@hooks/useCurrentUserInfo/types';
import { useTriggerFocus } from '@hooks/useModalPopoverFocus';
import { usePopoverFocus } from '@hooks/usePopoverFocus';
import { buildAddSnackbarAction } from '@context/SnackbarNotifications/actions';
import { useSnackbarNotifications } from '@context/SnackbarNotifications/SnackbarNotifications';
import { StyledMoreIcon } from '@components/shared/MoreIcon';
import { EVENT_CATEGORY, EVENT_ACTION } from '@lib/ga/types';
import { GA_PRODUCT_CATALOG } from '@helpers/constants';
import {
  AlbumMetadata,
  AlbumMetadataListResponse,
} from '@generated/grpc/spotify/artistwatchlist/v1';
import HeldReleasesRequestModal from '../HeldReleasesRequestModal';

type ActionTableCellProps = {
  row: AlbumMetadata;
  mutate: KeyedMutator<AlbumMetadataListResponse>;
  userLicensor: Resource | undefined;
};

export const ActionTableCell: FunctionComponent<
  ActionTableCellProps
> = props => {
  const { row, mutate, userLicensor } = props;
  const t = useT();
  const { copyText } = useClipboard(row.albumUri || '');
  const [, dispatch] = useSnackbarNotifications();
  const [requestedRelease, setRequestedRelease] = useState<AlbumMetadata>();

  const postCloseFocusRef = useRef<HTMLElement | null>(null);

  const { triggerBtnRef } = useTriggerFocus(!!requestedRelease);
  const {
    isOpen,
    toggleOn,
    toggleOff,
    openBtnRef,
    firstInteractiveEleRef: firstPopoverEleRef,
  } = usePopoverFocus();

  const handleOnRequestApproval = () => {
    setRequestedRelease(row);
    sendEvent({
      eventCategory: EVENT_CATEGORY.HELD_RELEASES_TABLE,
      eventAction: EVENT_ACTION.CLICK,
      eventLabel: `Held Releases Request Modal Opened`,
      product: GA_PRODUCT_CATALOG,
    });
    toggleOff();
  };

  const handleCopyURI = async () => {
    const didCopy = await copyText();

    if (didCopy) {
      dispatch(
        buildAddSnackbarAction(
          t(
            'atlas.snackbars.copied_to_clipboard',
            'Copied to clipboard',
            'Snackbar message to indicate copied to clipboard',
          ),
        ),
      );
    }
    toggleOff();
  };

  return (
    <>
      <PopoverTrigger
        aria-label="popover-trigger"
        placement="start"
        onShow={() => {
          sendEvent({
            eventCategory: EVENT_CATEGORY.HELD_RELEASES_TABLE,
            eventAction: EVENT_ACTION.CLICK,
            eventLabel: 'Held Release Action Clicked',
            product: GA_PRODUCT_CATALOG,
          });
          toggleOn();
        }}
        onHide={toggleOff}
        overlay={
          isOpen && (
            <PopoverNavigation arrow="end">
              <PopoverNavigationItem>
                <PopoverNavigationLink
                  ref={firstPopoverEleRef}
                  component="button"
                  onClick={handleCopyURI}
                >
                  {t(
                    'atlas.labels.copy_release_uri',
                    'Copy Release URI',
                    'Label for copy release URI',
                  )}
                </PopoverNavigationLink>
              </PopoverNavigationItem>
              {!row.dateSubmitted && (
                <PopoverNavigationItem>
                  <PopoverNavigationLink
                    component="button"
                    onClick={handleOnRequestApproval}
                  >
                    {t(
                      'atlas.labels.approve_release',
                      'Request approval',
                      'Label for approve release',
                    )}
                  </PopoverNavigationLink>
                </PopoverNavigationItem>
              )}
            </PopoverNavigation>
          )
        }
      >
        <ButtonTertiary
          aria-label="More"
          aria-expanded={isOpen}
          ref={e => {
            if (!!requestedRelease) {
              triggerBtnRef.current = e;
            } else {
              openBtnRef.current = e;
            }
            postCloseFocusRef.current = e;
          }}
          iconOnly={() => <StyledMoreIcon hasHoverBackground />}
        />
      </PopoverTrigger>
      {!!requestedRelease && (
        <HeldReleasesRequestModal
          requestedRelease={requestedRelease!}
          licensor={userLicensor}
          onClose={() => {
            setRequestedRelease(undefined);
            if (postCloseFocusRef && postCloseFocusRef.current) {
              postCloseFocusRef.current.focus();
            }
          }}
          refresh={mutate}
        />
      )}
    </>
  );
};
